import React from 'react';
import { H1, H3, colors, Icon } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  Amount,
} from './styles';

const COMPLIANCE_DASHBOARD_QUERY = gql`
  query ComplianceDashboard {
    forexComplianceRecords(limit: 100, offset: 0) {
      id
      buyer
      supplier
      country
      totalValue
      complianceStatus
      isLocked
    }
    customsDocuments(limit: 100, offset: 0) {
      id
      docId
      orderId
      documentType
      status
      clearanceStatus
    }
  }
`;

const formatAmount = (value: unknown) => {
  if (value === null || value === undefined || value === '') return '—';
  const numericValue = typeof value === 'number' ? value : Number(String(value).replace(/[^0-9.-]/g, ''));
  if (!Number.isFinite(numericValue)) return '—';
  return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const isResolved = (status?: string) => {
  const s = (status || '').toLowerCase();
  return s.includes('completed') || s.includes('cleared') || s.includes('approved');
};

export const ComplianceDashboard: React.FC = () => {
  const { data } = useGraphQLQuery<{
    forexComplianceRecords?: Array<{ id: string; buyer: string; supplier: string; country: string; totalValue: number; complianceStatus: string; isLocked: boolean }>;
    customsDocuments?: Array<{ id: number; docId?: string; orderId?: number; documentType?: string; status?: string; clearanceStatus?: string }>;
  }>(COMPLIANCE_DASHBOARD_QUERY, {});

  const forexRecords = data?.forexComplianceRecords ?? [];
  const customsDocs = data?.customsDocuments ?? [];

  const forexPending = forexRecords.filter((r) => !isResolved(r.complianceStatus)).length;
  const customsPending = customsDocs.filter((d) => !isResolved(d.clearanceStatus || d.status)).length;

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>Customs & Compliance</H1>
          <Subtitle>Forex remittance and customs filings across every order, and what still needs a decision.</Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      <StatsGrid>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Forex Records Pending</StatTitle>
              <StatValue>{forexPending}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="DollarSign" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Customs Documents Pending</StatTitle>
              <StatValue>{customsPending}</StatValue>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="ShieldCheck" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <OrdersHeader>
          <H3 style={{ margin: 0 }}>Forex Compliance Records</H3>
        </OrdersHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Buyer</TableHeader>
                <TableHeader>Supplier</TableHeader>
                <TableHeader>Country</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Value</TableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {forexRecords.slice(0, 20).map((r) => (
                <TableRow key={r.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                  <TableCell>{r.buyer ?? '—'}</TableCell>
                  <TableCell>{r.supplier ?? '—'}</TableCell>
                  <TableCell>{r.country ?? '—'}</TableCell>
                  <TableCell>{r.complianceStatus ?? '—'}</TableCell>
                  <TableCell><Amount>{formatAmount(r.totalValue)}</Amount></TableCell>
                </TableRow>
              ))}
              {forexRecords.length === 0 && (
                <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TableCell colSpan={5}><span style={{ color: colors.gray500 }}>No forex compliance records</span></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </OrdersCard>

      <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} style={{ marginTop: '1.5rem' }}>
        <OrdersHeader>
          <H3 style={{ margin: 0 }}>Customs Documents</H3>
        </OrdersHeader>
        <TableContainer>
          <Table>
            <TableHead>
              <tr>
                <TableHeader>Doc ID</TableHeader>
                <TableHeader>Order</TableHeader>
                <TableHeader>Type</TableHeader>
                <TableHeader>Status</TableHeader>
                <TableHeader>Clearance</TableHeader>
              </tr>
            </TableHead>
            <TableBody>
              {customsDocs.slice(0, 20).map((d) => (
                <TableRow key={d.id} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                  <TableCell>{d.docId ?? d.id}</TableCell>
                  <TableCell>{d.orderId ?? '—'}</TableCell>
                  <TableCell>{d.documentType ?? '—'}</TableCell>
                  <TableCell>{d.status ?? '—'}</TableCell>
                  <TableCell>{d.clearanceStatus ?? '—'}</TableCell>
                </TableRow>
              ))}
              {customsDocs.length === 0 && (
                <TableRow initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
                  <TableCell colSpan={5}><span style={{ color: colors.gray500 }}>No customs documents filed yet</span></TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </OrdersCard>
    </DashboardContainer>
  );
};
